import React, { useState, useEffect, ChangeEvent } from 'react';
import { Card, CardBody, Row, Col, Input, Label } from 'reactstrap';
import UserAutocomplete, { UtenteSearch } from '../../../Components/Autocomplete/UserAutocomplete';
import { Btn } from '../../../AbstractElements';
import type { AttivitaFilters } from '../../../Types/Panel.type';

type SelectedUser = { ID?: string; nominativo?: string; email?: string };

type CategoryOption = { id: number; label: string };

type Props = {
  filters: AttivitaFilters;
  onChange: (filters: AttivitaFilters) => void;
  statoOptions: CategoryOption[];
  tipoOptions: CategoryOption[];
  disabled?: boolean;
  onAddRow?: () => void;
};

export default function FiltriAttivita({
  filters,
  onChange,
  statoOptions,
  tipoOptions,
  disabled = false,
  onAddRow
}: Props) {
  const [showFilter, setShowFilter] = useState(Boolean(filters.attivitaID));
  const [localFilters, setLocalFilters] = useState<AttivitaFilters>(filters);

  useEffect(() => {
    setLocalFilters(filters);
    if (filters.attivitaID) setShowFilter(true);
  }, [filters]);

  const handleToggle = () => setShowFilter(prev => !prev);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const next = { ...localFilters, [name]: value } as AttivitaFilters;
    setLocalFilters(next);
  };

  // --- handlers per UserAutocomplete ---
  const handleAddUser = (u: UtenteSearch) => {
    // evita duplicati
    if (localFilters.utenti?.some(x => x.ID === u.ID)) return;

    const nextUsers: SelectedUser[] = [
      ...(localFilters.utenti ?? []),
      { ID: u.ID, nominativo: `${u.nome} ${u.cognome}`, email: u.email }
    ];

    setLocalFilters(prev => ({ ...prev, utenti: nextUsers }));
  };

  const handleRemoveUser = (id: string) => {
    const nextUsers = (localFilters.utenti ?? []).filter(x => x.ID !== id);
    setLocalFilters(prev => ({ ...prev, utenti: nextUsers }));
  };

  const handleApply = () => {
    onChange({ ...localFilters });
  };

  const handleReset = () => {
    const reset: AttivitaFilters = {
      attivitaID: '',
      ticket: false,
      ticketID: '',
      stato: '',
      tipo: '',
      data_svolgere: '',
      data_esecuzione: '',
      utenti: [] // <-- reset anche utenti
    };
    setLocalFilters(reset);
    onChange(reset);
  };

  const hasActive =
    !!filters.attivitaID ||
    filters.ticket ||
    !!filters.ticketID.trim() ||
    !!filters.stato ||
    !!filters.tipo ||
    !!filters.data_svolgere ||
    !!filters.data_esecuzione ||
    (filters.utenti?.length ?? 0) > 0;

  return (
    <>
      <div className='d-flex w-100 justify-content-between flex'>
        <Btn
          className="px-3"
          color={hasActive ? 'success' : 'primary'}
          type="button"
          onClick={handleToggle}
          disabled={disabled}
        >
          Filtri {hasActive && '(attivi)'}
        </Btn>

        {onAddRow && (
          <Btn
            className="px-3"
            color="success"
            type="button"
            onClick={onAddRow}
            disabled={disabled}
          >
            +
          </Btn>
        )}
      </div>

      <div className="mb-3 d-flex justify-content-between my-2 w-100">
        {showFilter && (
          <Card className="mt-3 shadow-sm w-100" style={{ backgroundColor: '#fcfcfc' }}>
            <CardBody>
              <Row className="g-3">
                <Col md={3}>
                  <Label htmlFor="f_attivita_id">ID</Label>
                  <Input
                    id="f_attivita_id"
                    type="number"
                    min={1}
                    name="attivitaID"
                    value={localFilters.attivitaID}
                    onChange={handleChange}
                    disabled={disabled}
                  />
                </Col>

                <Col md={6}>
                  <Label htmlFor="f_ticket_id">ID ticket</Label>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check mb-0 flex-shrink-0">
                      <Input id="f_ticket" type="checkbox" checked={localFilters.ticket} disabled={disabled} onChange={event => setLocalFilters(current => ({ ...current, ticket: event.target.checked }))} />
                      <Label check htmlFor="f_ticket">Ticket</Label>
                    </div>
                    <Input id="f_ticket_id" type="text" name="ticketID" placeholder="Cerca ID ticket" value={localFilters.ticketID} onChange={handleChange} disabled={disabled} />
                  </div>
                </Col>

                {/* Stato */}
                <Col md={3}>
                  <Label htmlFor="f_stato">Stato</Label>
                  <Input
                    id="f_stato"
                    type="select"
                    name="stato"
                    value={localFilters.stato}
                    onChange={handleChange}
                    disabled={disabled}
                  >
                    <option value="">Non Completati</option>
                    {statoOptions.map(opt => (
                      <option key={opt.id} value={String(opt.id)}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </Col>

                {/* Tipo */}
                <Col md={3}>
                  <Label htmlFor="f_tipo">Tipo</Label>
                  <Input
                    id="f_tipo"
                    type="select"
                    name="tipo"
                    value={localFilters.tipo}
                    onChange={handleChange}
                    disabled={disabled}
                  >
                    <option value="">Tutti</option>
                    {tipoOptions.map(opt => (
                      <option key={opt.id} value={String(opt.id)}>
                        {opt.label}
                      </option>
                    ))}
                  </Input>
                </Col>

                <Col md={3}>
                  <Label htmlFor="f_data_svolgere">Data da svolgere</Label>
                  <Input
                    id="f_data_svolgere"
                    type="date"
                    name="data_svolgere"
                    value={localFilters.data_svolgere}
                    onChange={handleChange}
                    disabled={disabled}
                  />
                </Col>

                <Col md={3}>
                  <Label htmlFor="f_data_esecuzione">Data esecuzione</Label>
                  <Input
                    id="f_data_esecuzione"
                    type="date"
                    name="data_esecuzione"
                    value={localFilters.data_esecuzione}
                    onChange={handleChange}
                    disabled={disabled}
                  />
                </Col>

                {/* Utenti (nuovo) */}
                <Col md={12}>
                  <Label>Utenti</Label>
                  <UserAutocomplete
                    selectedUsers={localFilters.utenti ?? []}
                    onAddUser={handleAddUser}
                    onRemoveUser={handleRemoveUser}
                  />
                </Col>
              </Row>

              <Row className="mt-3">
                <Col className="d-flex gap-2">
                  <Btn color="primary" type="button" onClick={handleApply} disabled={disabled}>
                    Applica filtri
                  </Btn>

                  <Btn color="secondary" type="button" onClick={handleReset} disabled={disabled}>
                    Reset filtri
                  </Btn>
                </Col>
              </Row>
            </CardBody>
          </Card>
        )}
      </div>
    </>
  );
}
